import { Button, VStack } from '@chakra-ui/react';
import { useEffect, useRef } from 'react';

import { LinkFields } from './LinkFields';
import { useCreateLink, type CreateLinkInput } from './useCreateLink';
import { useLinkFields } from './useLinkFields';

interface LinkCreateFormProps {
  readonly parent: CreateLinkInput['parent'];
  /** Analytics surface of the entry point. */
  readonly surface: string;
  /** The link was made; the caller closes its modal. */
  readonly onCreated: () => void;
  readonly onDestinationProblem?: (problem: 'gone' | 'forbidden') => void;
  readonly onBusyChange?: (busy: boolean) => void;
}

/**
 * New link: the fields and one primary action, rendered by the Create modal's
 * Link body.
 */
export function LinkCreateForm({
  parent,
  surface,
  onCreated,
  onDestinationProblem,
  onBusyChange,
}: LinkCreateFormProps) {
  const fields = useLinkFields();
  const { create, isPending } = useCreateLink({ surface, onDestinationProblem });
  const isBusy = isPending || fields.isResolving;
  const urlRef = useRef<HTMLInputElement>(null);
  // The address is why this form is open, so it takes focus at once.
  useEffect(() => urlRef.current?.focus(), []);
  useEffect(() => {
    onBusyChange?.(isBusy);
  }, [isBusy, onBusyChange]);

  // The final URL's title and site icon are resolved first, so a URL typed
  // inside the lookup debounce still gets the icon the form offers.
  const submit = async () => {
    if (isPending) return;
    const resolved = await fields.resolveValues();
    if (!resolved) return;
    const created = await create({ parent, ...resolved });
    if (created) onCreated();
  };

  return (
    <VStack spacing={4} align="stretch">
      <LinkFields
        fields={fields}
        isDisabled={isBusy}
        onSubmit={() => void submit()}
        urlInputRef={urlRef}
      />
      {/* No Cancel: the modal's own close already leaves. */}
      <Button
        variant="primary"
        isLoading={isBusy}
        loadingText="Adding…"
        isDisabled={!fields.values || isBusy}
        onClick={() => void submit()}
      >
        Add link
      </Button>
    </VStack>
  );
}
